"use client"

import type React from "react"

import { useAuth } from "@/components/auth-provider"
import { useRouter, usePathname } from "next/navigation"
import { useEffect } from "react"
import { Loader2, Shield } from "lucide-react"
import { RoleBasedRouter } from "@/components/role-based-router"

interface AuthGuardProps {
  children: React.ReactNode
}

export function AuthGuard({ children }: AuthGuardProps) {
  const { user, isLoading, isAuthenticated } = useAuth()
  const router = useRouter()
  const pathname = usePathname()

  useEffect(() => {
    console.log("🔍 [v0] AuthGuard effect triggered:", {
      pathname,
      isLoading,
      isAuthenticated,
      userExists: !!user,
      userEmail: user?.email,
      userRole: user?.role,
    })

    // Allow access to login page without authentication, but redirect if already authenticated
    if (pathname === "/login") {
      if (!isLoading && isAuthenticated && user) {
        console.log("🔍 [v0] AuthGuard: Already authenticated, redirecting from login based on role")
        // Redirect based on user role to prevent showing internal dashboard to clients
        switch (user.role) {
          case "organization":
          case "org":
            router.replace("/organization/dashboard")
            break
          case "client":
            router.replace("/client/dashboard")
            break
          case "internal":
          default:
            router.replace("/")
            break
        }
        return
      }
      console.log("🔍 [v0] AuthGuard: On login page, allowing access")
      return
    }

    if (!isLoading && !isAuthenticated) {
      console.log("🔒 [v0] AuthGuard: Not authenticated, redirecting to login")
      router.replace("/login")
    }
  }, [user, isLoading, isAuthenticated, router, pathname])

  // Allow login page to render without authentication, but redirect if already authenticated
  if (pathname === "/login") {
    if (isAuthenticated) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100">
          <div className="text-center space-y-4">
            <Loader2 className="h-6 w-6 animate-spin mx-auto text-blue-600" />
            <p className="text-gray-600">Redirecting to dashboard...</p>
          </div>
        </div>
      )
    }
    return <>{children}</>
  }

  // Block rendering while checking authentication OR if not authenticated
  // This prevents ANY component from mounting and making API calls before redirect happens
  // For authenticated users (isLoading=false, isAuthenticated=true), this doesn't block
  if (isLoading || !isAuthenticated) {
    return null
  }

  return <RoleBasedRouter>{children}</RoleBasedRouter>
}
